// Soil Map Units colored by harvest-equipment operability. USDA's WMS won't fill by attribute, so we
// pull geometry + rating from its tabular REST service and render the fill ourselves as a vector layer.

const SDA_URL = 'https://sdmdataaccess.sc.egov.usda.gov/Tabular/post.rest'
const RULE = 'FOR - Harvest Equipment Operability'

// USDA interphrc rating classes (must match SDA values verbatim) with our fill colors.
export const NOT_RATED = 'Not rated'

const RATING_COLORS = [
  ['Well suited', '#43A047'],
  ['Moderately suited', '#FB8C00'],
  ['Poorly suited', '#E53935'],
  [NOT_RATED, '#9E9E9E']
]

export const SOIL_OP_FILL_COLOR = [
  'match', ['get', 'rating'],
  ...RATING_COLORS.flatMap(([rating, color]) => [rating, color]),
  '#9E9E9E'
]
export const SOIL_OP_FILL_OPACITY = 0.35

export const SOIL_OP_LEGEND = RATING_COLORS.map(([rating, color]) => ({ rating, color }))

// Dominant-condition rating per map unit: the class covering the largest share of components by area.
const buildQuery = (wktPolygon) => `
  SELECT m.mupolygonkey, mu.musym, mu.muname, dc.rating, m.mupolygongeo.STAsText() AS wkt
  FROM mupolygon m
  JOIN mapunit mu ON mu.mukey = m.mukey
  OUTER APPLY (
    SELECT TOP 1 ci.interphrc AS rating
    FROM component c
    JOIN cointerp ci ON ci.cokey = c.cokey AND ci.ruledepth = 0 AND ci.mrulename = '${RULE}'
    WHERE c.mukey = mu.mukey AND c.comppct_r IS NOT NULL
    GROUP BY ci.interphrc ORDER BY SUM(c.comppct_r) DESC
  ) dc
  WHERE m.mupolygongeo.STIntersects(geometry::STGeomFromText('${wktPolygon}', 4326)) = 1`

// Regex-extracts coordinate rings for POLYGON/MULTIPOLYGON; holes render as their own rings (no donuts).
const ringsFrom = (wkt) =>
  (wkt.match(/\(([-0-9.,\sEe]+)\)/g) || [])
    .map(g => g.slice(1, -1).split(',').map(p => p.trim().split(/\s+/).map(Number)))

const wktToGeometry = (wkt) => {
  const rings = ringsFrom(wkt)
  if (!rings.length) return null
  return wkt.trimStart().toUpperCase().startsWith('MULTIPOLYGON')
    ? { type: 'MultiPolygon', coordinates: rings.map(r => [r]) }
    : { type: 'Polygon', coordinates: rings }
}

// bounds is an atlas BoundingBox [west, south, east, north]; signal cancels a stale scan.
export async function fetchHarvestOperability ([west, south, east, north], signal) {
  const poly = `POLYGON((${west} ${south},${east} ${south},${east} ${north},${west} ${north},${west} ${south}))`
  const res = await fetch(SDA_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ format: 'JSON+COLUMNNAME', query: buildQuery(poly) }),
    signal
  })
  if (!res.ok) throw new Error(`Soil Data Access responded ${res.status}`)
  const rows = ((await res.json()).Table || []).slice(1) // first row is column names
  return {
    type: 'FeatureCollection',
    features: rows.reduce((acc, [mupolygonkey, musym, muname, rating, wkt]) => {
      const geometry = wktToGeometry(wkt)
      if (geometry) {
        acc.push({
          type: 'Feature',
          id: mupolygonkey, // stable id so the viewport cache can dedupe across overlapping fetches
          geometry,
          properties: { musym, muname, rating: rating || NOT_RATED }
        })
      }
      return acc
    }, [])
  }
}
